import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { getMediaUrl } from './ProjectMedia'
import { registerTikTokPlayer, usePausePlayerOffscreen } from '../lib/videoPolicy'
import {
  CARD_MAX_PX,
  getCarouselVisualDepth,
  getScrimOpacity,
  getSlideStyle,
  normalizeOffset,
  SLIDE_TRANSITION,
  TRANSITION_EASING,
  TRANSITION_MS,
  VISIBLE_DEPTH,
} from '../lib/carouselCoverflow'
import { useCarouselMetrics } from '../lib/hooks/useCarouselMetrics'
import { getTikTokPlayerSrc, postToTikTokPlayer } from '../lib/socialCarouselCards'
import CarouselSocialStatsPanel from './CarouselSocialStatsPanel'
import InfoCircleIcon from './InfoCircleIcon'
import { PROJECT_TOPIC_LABEL_CLASS } from '../lib/projectTopicLabel'
import { IS_DESKTOP_EXPERIENCE } from '../lib/deviceGate'
import { IS_OWNER } from '../lib/ownerMode'

const SINGLE_STATS_PLACEMENT = {
  top: '50%',
  left: '50%',
  width: 'min(17rem, calc(100% - 2rem))',
  transform: 'translate(-50%, -50%)',
}

const FRAME_CLASS = {
  portrait:
    'carousel-media-frame site-frame site-media-frame relative aspect-[9/16] overflow-hidden',
  landscape:
    'carousel-media-frame site-frame site-media-frame relative aspect-video overflow-hidden',
}

const VIDEO_FRAME_INNER_CLASS = 'carousel-media-frame-inner relative h-full w-full overflow-hidden'

const stopVideoEvent = (event) => {
  if (event.target.closest('video, iframe')) event.stopPropagation()
}

const getPosterUrl = (item) => {
  if (!item?.poster) return undefined
  if (item.poster.startsWith('http')) return item.poster
  return getMediaUrl(item.poster)
}

const shapeWarnedIds = new Set()

// Every media item declares isShort; an absent flag is a data bug, not a default.
const isItemLandscape = (item) => {
  if (IS_OWNER && item && typeof item.isShort !== 'boolean' && !shapeWarnedIds.has(item.id)) {
    shapeWarnedIds.add(item.id)
    console.warn(`[carousel] isShort missing on media item "${item.id}", assuming landscape`)
  }

  return !item?.isShort
}

const getCarouselShape = (items) => (isItemLandscape(items[0]) ? 'landscape' : 'portrait')

const mixedWarnedIds = new Set()

// There are exactly two formats and a carousel is one of them throughout.
// An item that disagrees with the first is a data bug, not a mixed fan.
const warnOnMixedShape = (items, shape) => {
  if (!IS_OWNER) return

  const odd = items.find((item) => (isItemLandscape(item) ? 'landscape' : 'portrait') !== shape)
  if (!odd || mixedWarnedIds.has(odd.id)) return

  mixedWarnedIds.add(odd.id)
  console.warn(`[carousel] "${odd.id}" does not match the ${shape} carousel it sits in`)
}

const CarouselSlide = ({
  item,
  shape,
  depth,
  isHovered,
  isTransitioning,
  onControlsRef,
  onActivate,
  layout = 'fan',
}) => {
  const isCenter = depth === 0
  const tiktokId = item.tiktokId
  const isTikTok = Boolean(tiktokId)
  const videoRef = useRef(null)
  const iframeRef = useRef(null)
  const frameRef = useRef(null)
  const [thumbFailed, setThumbFailed] = useState(false)
  const videoUrl = !isTikTok ? getMediaUrl(item.video) : null
  const posterUrl = getPosterUrl(item)
  const scrimOpacity = getScrimOpacity(depth, isHovered, layout)
  const videoMediaClass = 'carousel-slide-media h-full w-full object-contain bg-secondary'
  const tiktokPosterClass = 'carousel-slide-media h-full w-full object-cover bg-[#0e0e10]'
  const tiktokIframeClass =
    'carousel-slide-media block h-full w-full border-0 bg-[#0e0e10]'
  // TikTok's player mounts once the card has landed and stopped moving, so the
  // visitor's single press is one TikTok honours with sound.
  const showTikTokPlayer = isCenter && isTikTok && !isTransitioning
  const playerSrc = useMemo(
    () => (showTikTokPlayer ? getTikTokPlayerSrc(tiktokId) : null),
    [showTikTokPlayer, tiktokId]
  )

  usePausePlayerOffscreen(
    videoRef,
    () => videoRef.current?.pause(),
    isCenter && !isTikTok
  )
  usePausePlayerOffscreen(
    frameRef,
    () => postToTikTokPlayer(iframeRef.current, 'pause'),
    showTikTokPlayer
  )

  useEffect(() => {
    if (!showTikTokPlayer) return undefined
    return registerTikTokPlayer(iframeRef.current)
  }, [showTikTokPlayer, playerSrc])

  useEffect(() => {
    if (!isCenter || !onControlsRef) return

    if (isTikTok) {
      onControlsRef.current = {
        pause: () => postToTikTokPlayer(iframeRef.current, 'pause'),
        play: () => postToTikTokPlayer(iframeRef.current, 'play'),
        video: null,
      }
      return
    }

    onControlsRef.current = {
      pause: () => videoRef.current?.pause(),
      play: () => videoRef.current?.play().catch(() => {}),
      video: videoRef.current,
    }
  }, [isCenter, isTikTok, onControlsRef, showTikTokPlayer, videoUrl])

  const handleSideKeyDown = (event) => {
    if (!onActivate) return

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onActivate()
    }
  }

  const renderPoster = (className) =>
    posterUrl && !thumbFailed ? (
      <img
        src={posterUrl}
        alt=""
        className={className}
        loading={isCenter ? 'eager' : 'lazy'}
        aria-hidden="true"
        onError={() => setThumbFailed(true)}
      />
    ) : (
      <div className="flex h-full w-full items-center justify-center p-4" aria-hidden="true">
        <p className="line-clamp-4 text-center text-xs normal-case leading-snug text-text-muted">
          {item.label}
        </p>
      </div>
    )

  if (!videoUrl && !isTikTok) return null

  return (
    <div
      ref={frameRef}
      role={onActivate ? 'button' : undefined}
      tabIndex={onActivate ? 0 : -1}
      aria-label={onActivate ? `Show ${item.label}` : undefined}
      className={[
        FRAME_CLASS[shape],
        'pointer-events-auto',
        onActivate ? 'cursor-pointer' : '',
        isHovered ? 'carousel-preview-slide-hovered' : '',
      ].join(' ')}
      style={{ width: 'var(--carousel-card-w)' }}
      onClick={onActivate}
      onKeyDown={handleSideKeyDown}
    >
      <div
        className={VIDEO_FRAME_INNER_CLASS}
        onClick={isCenter ? stopVideoEvent : undefined}
        onPointerDown={isCenter ? stopVideoEvent : undefined}
      >
        {isCenter && !isTikTok ? (
          /* Our own videos get the native player, like the single videos on the
             page: the visitor presses its play, not an overlay of ours. */
          <video
            ref={videoRef}
            className={videoMediaClass}
            src={videoUrl}
            poster={posterUrl}
            controls
            playsInline
            preload="metadata"
            onClick={stopVideoEvent}
            onPointerDown={stopVideoEvent}
            tabIndex={0}
            aria-label={item.label}
          />
        ) : showTikTokPlayer ? (
          <iframe
            ref={iframeRef}
            src={playerSrc}
            allow="autoplay; fullscreen; encrypted-media"
            allowFullScreen
            className={tiktokIframeClass}
            style={{ colorScheme: 'dark' }}
            title={item.label}
            aria-label={item.label}
          />
        ) : (
          /* Every other state is the same poster, so changing depth never
             remounts it and no side card ever holds a player. */
          renderPoster(isTikTok ? tiktokPosterClass : videoMediaClass)
        )}

        {layout === 'single' || scrimOpacity > 0 ? (
          <div
            className="carousel-slide-scrim pointer-events-none absolute inset-0"
            style={{
              backgroundColor: `rgba(0, 0, 0, ${scrimOpacity})`,
              transition:
                layout === 'single'
                  ? `background-color ${TRANSITION_MS}ms ${TRANSITION_EASING}`
                  : undefined,
            }}
            aria-hidden="true"
          />
        ) : null}

        {!showTikTokPlayer && !(isCenter && !isTikTok) && item.caption ? (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-3 pb-3 pt-10">
            <p className="line-clamp-2 text-left text-xs normal-case leading-snug text-white">
              {item.caption}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  )
}

const CaseStudyMediaCarousel = ({
  items = [],
  showSocialStats = false,
  heading,
}) => {
  const layout = IS_DESKTOP_EXPERIENCE ? 'fan' : 'single'
  const isSingle = layout === 'single'

  // Fan needs ±VISIBLE_DEPTH; single needs one neighbour each side (minDisplay 3).
  const realCount = items.length
  const minDisplay = isSingle ? 3 : VISIBLE_DEPTH * 2 + 1
  const expandFactor =
    !realCount || realCount >= minDisplay
      ? 1
      : Math.ceil(minDisplay / realCount)

  const displayItems = (() => {
    if (!realCount) return []

    if (expandFactor === 1) {
      return items.map((item, sourceIndex) => ({
        item,
        sourceIndex,
        key: String(item.id),
      }))
    }

    const expanded = []
    for (let repeat = 0; repeat < expandFactor; repeat += 1) {
      items.forEach((item, sourceIndex) => {
        expanded.push({
          item,
          sourceIndex,
          key: `${item.id}__r${repeat}`,
        })
      })
    }
    return expanded
  })()

  const displayCount = displayItems.length
  // Start on a middle clone so both sides are populated immediately
  const initialIndex = expandFactor > 1 ? realCount : 0

  const [activeIndex, setActiveIndex] = useState(initialIndex)
  const [stackIndex, setStackIndex] = useState(initialIndex)
  const [hoveredIndex, setHoveredIndex] = useState(null)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [statsOpen, setStatsOpen] = useState(false)
  const statsPanelId = useId()
  const carouselRef = useRef(null)
  const centerControlsRef = useRef(null)
  const transitionTimerRef = useRef(null)
  const midpointTimerRef = useRef(null)
  const swipeStartRef = useRef(null)
  const swipeConsumedRef = useRef(false)

  // Shape is a property of the carousel, not of each card.
  const shape = getCarouselShape(items)
  const metrics = useCarouselMetrics(carouselRef, shape, layout)
  warnOnMixedShape(items, shape)

  useEffect(() => {
    return () => {
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)
      if (midpointTimerRef.current) clearTimeout(midpointTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (!displayCount) return

    const nextIndex = Math.min(activeIndex, displayCount - 1)
    if (nextIndex !== activeIndex) {
      setActiveIndex(nextIndex)
      setStackIndex(nextIndex)
    }
  }, [displayCount, activeIndex])

  const activeSourceIndex = displayCount
    ? (displayItems[Math.min(activeIndex, displayCount - 1)]?.sourceIndex ?? 0)
    : 0

  const currentItem = items[activeSourceIndex] || items[0]

  if (!realCount || !displayCount) return null

  const currentSocialStats = currentItem?.socialStats

  const navigateTo = (index) => {
    if (index === activeIndex || isTransitioning) return

    centerControlsRef.current?.pause()
    setHoveredIndex(null)
    setIsTransitioning(true)
    setActiveIndex(index)

    if (midpointTimerRef.current) clearTimeout(midpointTimerRef.current)
    if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current)

    midpointTimerRef.current = setTimeout(() => {
      setStackIndex(index)
    }, TRANSITION_MS / 2)

    transitionTimerRef.current = setTimeout(() => {
      setIsTransitioning(false)
    }, TRANSITION_MS)
  }

  const navigateToSource = (sourceIndex) => {
    if (sourceIndex === activeSourceIndex && !isTransitioning) return

    let bestIndex = activeIndex
    let bestDistance = Number.POSITIVE_INFINITY

    displayItems.forEach((entry, index) => {
      if (entry.sourceIndex !== sourceIndex) return

      const distance = Math.abs(normalizeOffset(index, activeIndex, displayCount))
      if (distance < bestDistance) {
        bestDistance = distance
        bestIndex = index
      }
    })

    navigateTo(bestIndex)
  }

  const handlePrevious = () => {
    navigateTo((activeIndex - 1 + displayCount) % displayCount)
  }

  const handleNext = () => {
    navigateTo((activeIndex + 1) % displayCount)
  }

  const handleSelect = (sourceIndex) => {
    navigateToSource(sourceIndex)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      handlePrevious()
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault()
      handleNext()
    }
  }

  const handleViewportPointerDown = (event) => {
    if (!isSingle) return
    if (event.target.closest('video, iframe, button, a, [role="dialog"]')) {
      swipeStartRef.current = null
      return
    }

    swipeConsumedRef.current = false
    swipeStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      id: event.pointerId,
    }
  }

  const handleViewportPointerUp = (event) => {
    const start = swipeStartRef.current
    swipeStartRef.current = null
    if (!isSingle || !start || event.pointerId !== start.id) return

    const dx = event.clientX - start.x
    const dy = event.clientY - start.y
    if (Math.abs(dx) <= 40 || Math.abs(dx) <= Math.abs(dy)) return

    swipeConsumedRef.current = true
    if (dx < 0) handleNext()
    else handlePrevious()
  }

  const handleViewportPointerCancel = () => {
    swipeStartRef.current = null
  }

  return (
    <figure
      ref={carouselRef}
      className="carousel-root space-y-6"
      style={
        metrics
          ? {
              '--carousel-card-w': `${metrics.cardW}px`,
              '--carousel-card-h': `${metrics.cardH}px`,
              '--carousel-perspective': `${metrics.perspective}px`,
              // 1 at full size; overlays scale with the card instead of clamping
              '--carousel-scale': metrics.cardW / CARD_MAX_PX[shape],
            }
          : undefined
      }
      tabIndex={0}
      onKeyDown={handleKeyDown}
      aria-roledescription="carousel"
      aria-label="Case study video carousel"
    >
      <div>
        {heading ? (
          <h2 className={PROJECT_TOPIC_LABEL_CLASS}>{heading.toLowerCase()}</h2>
        ) : null}
        <p className="site-muted mt-2 normal-case" aria-live="polite">
          {currentItem.label} · {activeSourceIndex + 1} / {realCount}
        </p>
      </div>

      <div className={isSingle ? 'carousel-scene relative py-10' : 'carousel-scene relative px-4 py-10'}>
        <div
          className="carousel-viewport relative mx-auto"
          style={{
            height: 'var(--carousel-card-h)',
            touchAction: isSingle ? 'pan-y' : undefined,
          }}
          onPointerDown={isSingle ? handleViewportPointerDown : undefined}
          onPointerUp={isSingle ? handleViewportPointerUp : undefined}
          onPointerCancel={isSingle ? handleViewportPointerCancel : undefined}
        >
          <div
            className="carousel-track pointer-events-none relative h-full w-full"
            style={{ perspective: 'var(--carousel-perspective)' }}
          >
            {metrics ? displayItems.map((entry, index) => {
              const offset = normalizeOffset(index, activeIndex, displayCount)
              const stackOffset = normalizeOffset(index, stackIndex, displayCount)
              const depth = getCarouselVisualDepth(
                offset,
                stackOffset,
                isTransitioning,
                layout
              )
              const isCenter = depth === 0
              const isInteractiveSide =
                !isCenter &&
                !isTransitioning &&
                (isSingle ? depth === 1 : depth <= VISIBLE_DEPTH)
              const isSideHovered = !isSingle && isInteractiveSide && hoveredIndex === index
              const slideStyle = getSlideStyle(
                offset,
                stackOffset,
                isSideHovered,
                isTransitioning,
                shape,
                metrics.cardW,
                layout
              )

              return (
                <div
                  key={entry.key}
                  data-is-center={isCenter ? 'true' : 'false'}
                  className="carousel-slide pointer-events-auto absolute left-1/2 top-1/2"
                  style={{
                    ...slideStyle,
                    transition: SLIDE_TRANSITION,
                  }}
                  onMouseEnter={
                    !isSingle && isInteractiveSide ? () => setHoveredIndex(index) : undefined
                  }
                  onMouseLeave={
                    !isSingle && isInteractiveSide ? () => setHoveredIndex(null) : undefined
                  }
                >
                  <CarouselSlide
                    item={entry.item}
                    shape={shape}
                    depth={depth}
                    isHovered={isSideHovered}
                    isTransitioning={isTransitioning}
                    onControlsRef={centerControlsRef}
                    onActivate={
                      isInteractiveSide
                        ? () => {
                            if (swipeConsumedRef.current) return
                            navigateTo(index)
                          }
                        : undefined
                    }
                    layout={layout}
                  />
                </div>
              )
            }) : null}
          </div>

          {showSocialStats ? (
            <CarouselSocialStatsPanel
              panelId={statsPanelId}
              open={statsOpen}
              onClose={() => setStatsOpen(false)}
              stats={currentSocialStats}
              videoLabel={currentItem?.label}
              isLandscape={shape === 'landscape'}
              frameStyle={isSingle ? SINGLE_STATS_PLACEMENT : undefined}
            />
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <div
          className={
            isSingle
              ? 'carousel-control-plate carousel-control-plate--single'
              : 'carousel-control-plate'
          }
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className={
              isSingle
                ? 'site-label min-h-[44px] px-2 py-1 transition-opacity hover:opacity-70'
                : 'site-label px-2 py-1 transition-opacity hover:opacity-70'
            }
            onClick={handlePrevious}
            aria-label="Previous video"
            disabled={isTransitioning}
          >
            ← prev
          </button>

          {isSingle ? (
            <p className="site-muted tabular-nums" aria-live="polite">
              {activeSourceIndex + 1} / {realCount}
            </p>
          ) : (
            <div className="flex items-center gap-2" aria-label="Video slides">
              {items.map((item, sourceIndex) => (
                <button
                  key={item.id}
                  type="button"
                  aria-label={`Show ${item.label}`}
                  aria-current={sourceIndex === activeSourceIndex ? 'true' : undefined}
                  disabled={isTransitioning}
                  className={[
                    'h-2 w-2 rounded-full transition-opacity',
                    sourceIndex === activeSourceIndex
                      ? 'bg-text-primary opacity-100'
                      : 'bg-text-primary opacity-30',
                  ].join(' ')}
                  onClick={() => handleSelect(sourceIndex)}
                />
              ))}
            </div>
          )}

          <button
            type="button"
            className={
              isSingle
                ? 'site-label min-h-[44px] px-2 py-1 transition-opacity hover:opacity-70'
                : 'site-label px-2 py-1 transition-opacity hover:opacity-70'
            }
            onClick={handleNext}
            aria-label="Next video"
            disabled={isTransitioning}
          >
            next →
          </button>
        </div>

        {showSocialStats ? (
          <div
            className="carousel-control-plate"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="site-label px-1 py-1 transition-opacity hover:opacity-70"
              onClick={() => setStatsOpen((open) => !open)}
              aria-label={statsOpen ? 'Hide social media stats' : 'Show social media stats'}
              aria-expanded={statsOpen}
              aria-controls={statsPanelId}
              aria-pressed={statsOpen}
            >
              stats
            </button>
            <button
              type="button"
              className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-current text-text-muted transition hover:text-text-primary"
              onClick={() => setStatsOpen((open) => !open)}
              aria-label={statsOpen ? 'Hide social media stats' : 'Show social media stats'}
              aria-expanded={statsOpen}
              aria-controls={statsPanelId}
              aria-pressed={statsOpen}
              title="Per-platform views, likes, comments, and saves for the active carousel video."
            >
              <InfoCircleIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : null}
      </div>
    </figure>
  )
}

export default CaseStudyMediaCarousel
